import React, { useState, useEffect } from 'react';
import { stringAvatar, stringToColor } from '../../../utils/avatarUtils';
import { CardGridSkeleton } from '../../loading/skeletons';
import './ClientGallery.css';

const ClientGallery = ({ 
  clients, 
  onViewProfile,
  onRestoreClient,
  listMode = 'active',
  isLoading,
  emptyStateMessage,
  onAddClient
}) => {
  const [avatarImages, setAvatarImages] = useState({});

  // Fetch avatar URLs for clients
  useEffect(() => {
    const fetchAvatarUrls = async () => {
      const newAvatarImages = { ...avatarImages };
      let hasUpdates = false;
      
      for (const client of clients) {
        // If client already has avatarUrl, use it
        if (client.avatarUrl && !newAvatarImages[client.id]) {
          newAvatarImages[client.id] = client.avatarUrl;
          hasUpdates = true;
          continue;
        }
        
        // Skip if we already have the avatar URL
        if (newAvatarImages[client.id]) {
          continue;
        }
        
        try {
          if (client.avatarUrl) {
            newAvatarImages[client.id] = client.avatarUrl;
            hasUpdates = true;
          }
        } catch (error) {
          console.error(`Error fetching avatar for client ${client.id}:`, error);
        }
      }
      
      if (hasUpdates) {
        setAvatarImages(newAvatarImages);
      }
    };

    if (clients.length > 0) {
      fetchAvatarUrls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const getInitials = (firstName, lastName) => {
    const first = firstName?.[0]?.toUpperCase() || '';
    const last = lastName?.[0]?.toUpperCase() || '';
    return first + last || '?';
  };

  const getAvatarColor = (name) => {
    return stringToColor(name);
  };

  if (isLoading) {
    return (
      <div className="clientGallery">
        <CardGridSkeleton count={12} itemSize={72} label="Loading clients" />
      </div>
    );
  }

  if (clients.length === 0) {
    const emptyTitle = listMode === 'archived' ? 'No archived clients' : 'No clients found';
    const emptySubtitle =
      emptyStateMessage || (listMode === 'archived' ? '' : 'Add your first client to get started');

    return (
      <div className="clientGallery">
        <div className="clientGalleryEmpty">
          <div className="clientGalleryEmptyIcon">👤</div>
          <h3>{emptyTitle}</h3>
          {emptySubtitle && emptySubtitle !== emptyTitle ? <p>{emptySubtitle}</p> : null}
          {onAddClient && listMode !== 'archived' && (
            <button className="clientGalleryAddBtn" onClick={onAddClient}>
              Add Client
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="clientGallery">
      <div className="clientGalleryGrid">
        {clients.map((client) => {
          const fullName = `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unnamed Client';
          const avatarUrl = client.avatarUrl || avatarImages[client.id];
          const initials = getInitials(client.firstName, client.lastName);
          const bgColor = getAvatarColor(fullName);

          return (
            <div
              key={client.id}
              className={`clientGalleryItem${listMode === 'archived' ? ' clientGalleryItem--archived' : ''}`}
            >
              <div
                className="clientGalleryItemMain"
                onClick={() => onViewProfile(client)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onViewProfile(client);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`View ${fullName}`}
              >
                <div className="clientGalleryImageWrapper">
                {avatarUrl ? (
                  <img 
                    src={avatarUrl} 
                    alt={fullName}
                    className="clientGalleryImage"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="clientGalleryAvatar"
                  style={{ 
                    backgroundColor: bgColor,
                    display: avatarUrl ? 'none' : 'flex'
                  }}
                >
                  {initials}
                </div>
              </div>
              <div className="clientGalleryName">{fullName}</div>
              </div>
              {listMode === 'archived' && onRestoreClient ? (
                <button
                  type="button"
                  className="clientGalleryRestoreBtn"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRestoreClient(client);
                  }}
                >
                  Restore
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientGallery;
